import React from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'next-i18next';
import { GroupChannel } from '@sendbird/chat/groupChannel';

import MoreIcon from 'src/components/news/MoreIcon';
import TooltipButton from 'src/components/ui/TooltipButton';
import Icon from 'src/components/ui/Icon';
import Typography from 'src/components/ui/Typography';
import Menu, { MenuItem } from 'src/components/ui/Menu';
import ReportModal from 'src/components/ui/ReportModal';
import EditPostModal from 'src/components/news/posts/EditPostModal';
import DeletePostModal from 'src/components/news/posts/DeletePostModal';
import ConfirmModal from 'src/components/ui/ConfirmModal';
import SearchAllUsersAndGroupsModal from 'src/components/ui/SearchAllUsersAndGroupsModal';
import { ExternalShareModal } from 'src/components/ad/ExternalShareModal';
import TinyPreviewCard from 'src/components/ui/TinyPreviewCard/TinyPreviewCard';
import { type PostsQuery, type Post, type Medium } from 'src/types/__generated__/graphql';
import stopPropagation from 'src/utils/stopPropagation';
import useUser from 'src/hooks/useUser';
import useAlerts from 'src/hooks/useAlerts';
import useAppRouter from 'src/hooks/useAppRouter';
import useModalOpenState, { useShareAsPrivateMessageModal, useShareViaModal } from 'src/hooks/useModalOpenState';
import { useAddContentEventMutation, useAddBlockedUserMutation } from 'src/hooks/__generated__/queries';
import { routePathContactsWithChannelUrl, routePathContactsWithUserId } from 'src/constants/router';
import { colours } from 'src/constants/colours';
import useFollowUnfollow from 'src/hooks/useFollowUnfollow';
import { getFollowUnfollowMenuOptions } from 'src/utils/popupMenuUtil';
import type { MenuOption } from 'src/types/MenuOption';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';
import useBreakpoints from 'src/hooks/useBreakpoints';

type Props = {
    post: ArrayElement<PostsQuery['posts']>;
};
type PostMenuOption = MenuOption & { key: string; iconColor?: string };

const PostMenu: React.FC<Props> = ({ post }) => {
    const { t } = useTranslation(['news', 'ad', 'profile']);
    const currentUser = useUser();
    const router = useAppRouter();
    const [addContentEventMutation] = useAddContentEventMutation();
    const { logEventDynamic } = useDynamicLogEvents();

    const followUnfollowParams = useFollowUnfollow({ user: post.owner?.user ?? undefined });

    const isOwner = post.iHaveOwnership;
    const { addAlert } = useAlerts();
    const isInFavoritePage = Boolean(router.pathname === '/favorites/[id]');

    const { isMobile } = useBreakpoints();

    const { props: searchContactModalProps, open: openSearchContactModal } = useShareAsPrivateMessageModal();
    const { props: shareModalProps, open: openShareModal } = useShareViaModal();
    const { props: deleteConfirmModalProps, open: openDeleteConfirmModal } = useModalOpenState();
    const { props: editPostModalProps, open: openEditPostModal } = useModalOpenState();
    const { props: reportModalProps, open: openReportModal } = useModalOpenState();
    const { props: confirmModalProps, open: openconfirmModal } = useModalOpenState();

    const [addBlockedUserMutation] = useAddBlockedUserMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
        onCompleted() {
            addAlert({
                type: 'success',
                message: t('profile:snackbarBlockUser', { name: post.owner?.displayName }),
            });
        },
    });

    const handleAddBlockedContact = async () => {
        await addBlockedUserMutation({
            variables: {
                userId: post.owner?.user?.id as string,
            },
            refetchQueries: ['NewsFeed', 'StoriesGroupedByOwners'],
            awaitRefetchQueries: true,
        });
        logEventDynamic(AnalyticsEvent.Block);
    };

    const followUnfollowMenuOptions: PostMenuOption[] =
        !isOwner && currentUser && post.owner?.user
            ? Object.entries(getFollowUnfollowMenuOptions(followUnfollowParams, isMobile || isInFavoritePage)).map(
                  ([key, plusMenuOption]) => ({ ...plusMenuOption, key }),
              )
            : [];

    const menuOptions: PostMenuOption[] = [
        {
            condition: Boolean(currentUser),
            key: 'ShareViaButton',
            handleClick: openShareModal,
            iconName: 'ExternalLink',
            mainTranslationKey: 'common:shareVia',
        },
        {
            condition: Boolean(currentUser),
            key: 'SearchAllUsersAndGroupsModal',
            handleClick: openSearchContactModal,
            iconName: 'ChatPrimaryInactive',
            mainTranslationKey: 'common:shareInPrivateMessage',
        },
        {
            condition: Boolean(isOwner),
            key: 'deletePost',
            handleClick: openDeleteConfirmModal,
            iconName: 'Delete',
            iconColor: colours.primary.main,
            mainTranslationKey: 'news:deletePost',
            menuItemProps: { disabled: !isOwner },
        },
        {
            condition: Boolean(isOwner),
            key: 'editPost',
            handleClick: openEditPostModal,
            iconName: 'Edit',
            mainTranslationKey: 'news:editPost',
        },
        {
            condition: Boolean(!isOwner && currentUser),
            key: 'reportPost',
            handleClick: openReportModal,
            iconName: 'Alert',
            mainTranslationKey: 'common:report.reportPost',
        },
        {
            condition: Boolean(!isOwner && currentUser && !post.owner?.company),
            key: 'blockUser',
            handleClick: openconfirmModal,
            iconName: 'Prohibited',
            mainTranslationKey: 'profile:blockUser',
        },
        ...followUnfollowMenuOptions,
    ];

    const menu = menuOptions
        .filter(({ condition }) => condition)
        .map(({ key, handleClick, menuItemProps, iconName, iconColor, mainTranslationKey }) => (
            <MenuItem key={key} onClick={handleClick} {...(menuItemProps ?? {})} data-target-id={`postMenu-${key}`}>
                <Icon name={iconName} size="1rem" color={iconColor} />
                <Box ml={1}>
                    <Typography variant="h6">{t(mainTranslationKey)}</Typography>
                </Box>
            </MenuItem>
        ));

    return (
        <Box
            data-target="btn-follow"
            data-target-id={`btn-follow-id-${post.id}`}
            display="flex"
            flexDirection="row"
            alignItems="center"
            onClick={stopPropagation}
        >
            <Menu id="more-menu" items={menu} data-target-id="post-moreButton-menu">
                <TooltipButton
                    title={t('common:tooltips.plus')}
                    ButtonComponent={MoreIcon}
                    buttonProps={{
                        ariaLabel: 'settings',
                        ariaControls: `post-${post.id}`,
                        ariaHasPopup: 'true',
                        'data-target-id': 'post-cardActions-moreButton',
                    }}
                />
            </Menu>
            {deleteConfirmModalProps.open && <DeletePostModal {...deleteConfirmModalProps} postId={post.id} />}
            {editPostModalProps.open && <EditPostModal {...editPostModalProps} post={post} />}
            {reportModalProps.open && <ReportModal {...reportModalProps} postId={post.id} />}
            {shareModalProps.open && post.owner && (
                <ExternalShareModal
                    {...shareModalProps}
                    externalShareTitle={post.owner.displayName}
                    shareContentProps={{
                        type: 'post',
                        id: post.id,
                    }}
                />
            )}

            {searchContactModalProps.open && post.owner && (
                <SearchAllUsersAndGroupsModal
                    title={t('common:shareInPrivateMessage')}
                    topArea={
                        <Box>
                            <TinyPreviewCard
                                iconName="PostIconCircle"
                                type="post"
                                post={post as Post}
                                media={post.media as Medium[]}
                                subtitle={post.owner.displayName}
                            />
                        </Box>
                    }
                    submitLabel={t('ad:share')}
                    onSelection={async (selectedRecipient) => {
                        const contactRoute =
                            selectedRecipient instanceof GroupChannel
                                ? `${routePathContactsWithChannelUrl}${selectedRecipient.url}`
                                : `${routePathContactsWithUserId}${selectedRecipient.id}`;
                        await router.push(`${contactRoute}&sharedPostId=${post.id}`);
                        await addContentEventMutation({
                            variables: {
                                postId: post.id,
                                eventType: 'shared_internally',
                            },
                            fetchPolicy: 'no-cache',
                        });
                    }}
                    renderGroupConversationButton
                    showConversations
                    {...searchContactModalProps}
                />
            )}
            {confirmModalProps.open && post.owner && (
                <ConfirmModal
                    title={t('profile:titleBlockUser', {
                        name: post.owner.displayName,
                    })}
                    description={t('profile:descriptionBlockUser', {
                        name: post.owner.displayName,
                    })}
                    onClick={handleAddBlockedContact}
                    confirm={t('common:modal.confirm')}
                    {...confirmModalProps}
                />
            )}
        </Box>
    );
};

export default PostMenu;
